import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavigateFunction, To } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import log from 'loglevel'
import { EvidenceGroup } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { GravatarWithTooltip } from 'src/components/Gravatar/Gravatar'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { CustomFileTypeFilterModal } from 'src/components/Reusable/IndexPage/FilterBar/modals/CustomFileTypeFilterModal'
import { FileTypeFilterProvider } from 'src/pages/Evidence/context/FileTypeFilterContext'
import {
  NavigateProvider,
  useNavigateContext,
} from 'src/components/Reusable/Navigation/NavigateContext'
import { SearchProvider } from 'src/Utils/search/search.context'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { getEvidenceSourceLabel } from 'src/Utils/Evidence/evidence.helpers'
import { useFileTypeIconForEvidenceGroups } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { EvidenceIndexGridHeader } from 'src/pages/Evidence/grid/EvidenceGridHeader'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { formatISODateTime, formatTimestamp } from 'src/Utils/formatDate'
import { formatEvidenceCaption } from 'src/Utils/helpers/string.helpers'
import {
  MultiSelectModal,
  MultiSelectState,
} from 'src/components/Reusable/MultiSelectModal'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { showInfoToast } from 'src/Utils/helpers/toast'
import {
  useLatestEvidenceGroups,
  useLatestEvidenceGroupsRequestFromParsedQuery,
} from 'src/pages/Evidence/evidence.hooks'
import {
  OutOfWindowNotice,
  ReadOnlyRelevantDate,
} from '../evidence.window.components'
import { EvidenceCellText } from './LinkExistingEvidenceModal.styles'

const useSearchableLatestEvidenceGroups = (evidenceGroups: EvidenceGroup[]) =>
  useMemo(
    () =>
      prepareSearchableData(
        evidenceGroups,
        (group) => group.getEvidenceId(),
        (group) => {
          const sourceText = getEvidenceSourceLabel(
            group.getIsAutomated(),
            group.getIsLinkedDocument(),
            group.getServiceModelId(),
            group.getDocImportLink(),
            group.getMime(),
          )

          return [
            group.getCaption(),
            // What the row shows: an automated caption's "--" reads "-".
            formatEvidenceCaption(
              group.getCaption(),
              group.getIsAutomated(),
            ) as string,
            sourceText,
            group.getActorEmail(),
            group.getActorName(),
            group.getRelevantDate()
              ? formatISODateTime(group.getRelevantDate())
              : '',
            group.getCreatedAt() ? formatISODateTime(group.getCreatedAt()) : '',
          ]
        },
        (group) => [group.getContentId() || ''],
      ),
    [evidenceGroups],
  )

/**
 * Generic modal that links existing account evidence (minus a caller-supplied
 * exclusion set) to something, via a caller-supplied `onLink` callback.
 * Presents every evidence object in the account in a filterable / searchable
 * multi-select list, reusing the evidence index's FilterBar + columns.
 * Filter/search state is isolated to the modal via a NavigateProvider that
 * writes query params to local state instead of the page URL.
 */
export const LinkExistingEvidenceModal = ({
  modalId,
  title,
  description,
  excludedEvidenceIds,
  onLink,
  canLink,
}: {
  modalId: ModalFormId
  title: string
  description: string
  excludedEvidenceIds: Set<string>
  onLink: (evidenceIds: string[]) => Promise<void>
  canLink: (evidence: EvidenceGroup) => boolean
}): JSX.Element => {
  const show = useIsShowModal(modalId)
  const [parsedQueryParams, setParsedQueryParams] = useState<ParsedQuery>({})
  // Clear the modal's local filter/search state on close so a fresh open always
  // starts unfiltered.
  const hide = useHideModal({
    modalId,
    onHide: () => setParsedQueryParams({}),
  })

  const navigate: NavigateFunction = useCallback((to: To | number) => {
    if (typeof to === 'number') {
      return
    }
    const search =
      typeof to === 'string'
        ? new URL(to, window.location.origin).search
        : to.search || ''
    setParsedQueryParams(queryString.parse(search, { arrayFormat: 'bracket' }))
  }, [])

  const locationSearchString = useMemo(() => {
    const search = queryString.stringify(parsedQueryParams, {
      arrayFormat: 'bracket',
    })
    return search && `?${search}`
  }, [parsedQueryParams])

  return (
    <NavigateProvider
      navigate={navigate}
      parsedQueryParams={parsedQueryParams}
      locationSearchString={locationSearchString}
    >
      <SearchProvider>
        <LinkExistingEvidenceModalContent
          modalId={modalId}
          title={title}
          description={description}
          excludedEvidenceIds={excludedEvidenceIds}
          onLink={onLink}
          canLink={canLink}
          show={show}
          hide={hide}
        />
      </SearchProvider>
    </NavigateProvider>
  )
}

const LinkExistingEvidenceModalContent = ({
  modalId,
  title,
  description,
  excludedEvidenceIds,
  onLink,
  canLink,
  show,
  hide,
}: {
  modalId: ModalFormId
  title: string
  description: string
  excludedEvidenceIds: Set<string>
  onLink: (evidenceIds: string[]) => Promise<void>
  canLink: (evidence: EvidenceGroup) => boolean
  show: boolean
  hide: () => void
}): JSX.Element => {
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  // The audit period still bounds the list, but the picker is not narrowed to evidence the
  // audit's controls already reach: that would leave only evidence that is already linked
  // on offer, which is the one thing nobody opens this modal to find.
  const req = useLatestEvidenceGroupsRequestFromParsedQuery(
    parsedQueryParams ?? {},
    { auditScopesDatesOnly: true },
  )
  const fileTypeIcon = useFileTypeIconForEvidenceGroups()

  // Only fetch while the modal is open.
  const { data, isLoading, error } = useLatestEvidenceGroups(req, show)

  const evidenceGroups = useMemo(
    () =>
      (data?.getItemsList() ?? [])
        .map((wrapper) => wrapper.getEvidenceGroup())
        .filter((group): group is EvidenceGroup => !!group)
        .filter((group) => !excludedEvidenceIds.has(group.getEvidenceId())),
    [data, excludedEvidenceIds],
  )

  // Server filters can remove selected evidence from the latest response.
  // Retain its metadata for this open session and refresh it whenever it returns.
  const evidenceById = useRef(new Map<string, EvidenceGroup>())
  useEffect(() => {
    if (!show) {
      evidenceById.current.clear()
      return
    }
    evidenceGroups.forEach((group) => {
      evidenceById.current.set(group.getEvidenceId(), group)
    })
  }, [evidenceGroups, show])

  const searchableEvidence = useSearchableLatestEvidenceGroups(evidenceGroups)
  const { data: matchingEvidenceGroups } = useApplySearch(searchableEvidence)

  const items = useMemo(
    () =>
      matchingEvidenceGroups.map((group) => {
        const FileTypeIcon = fileTypeIcon(
          group.getIsAutomated(),
          group.getMime(),
          group.getIsLinkedDocument(),
          group.getServiceModelId(),
        )
        const relevantDate = group.getRelevantDate()
        const createdAt = group.getCreatedAt()

        return {
          id: group.getEvidenceId(),
          disabled: !canLink(group),
          renderItems: [
            <FileTypeIcon width="24px" height="24px" key="0-icon" />,
            <EvidenceCellText key="1-title">
              {
                formatEvidenceCaption(
                  group.getCaption(),
                  group.getIsAutomated(),
                ) as string
              }
            </EvidenceCellText>,
            <EvidenceCellText key="2-controls-count">
              {group.getLinkedControlsCount() || 0}
            </EvidenceCellText>,
            <GravatarWithTooltip
              email={group.getActorEmail()}
              name={group.getActorName()}
              key="3-actor"
            />,
            relevantDate ? (
              <ReadOnlyRelevantDate
                key="4-relevant-date"
                relevantDate={relevantDate}
                windowStatus={group.getWindowStatus()}
                tooltipId={group.getEvidenceId()}
                InWindowWrapper={EvidenceCellText}
              />
            ) : null,
            createdAt ? (
              <EvidenceCellText key="5-created-at">
                {formatTimestamp(createdAt, DATE_FORMATS.ISO_WITH_TIME)}
              </EvidenceCellText>
            ) : null,
          ],
        }
      }),
    [matchingEvidenceGroups, fileTypeIcon, canLink],
  )

  const outOfWindowCount = data?.getOutOfWindowCount() ?? 0

  // Rendered through the filters slot, which sits outside the item grid — headers render
  // as grid children and a Message there would break the columns. The notice toggles
  // through this modal's own NavigateProvider, so Show All filters the picker without
  // touching the page behind it.
  const filters = useMemo(
    () => (
      <FileTypeFilterProvider>
        <>
          <FilterBar
            config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.EVIDENCE]}
            searchBarOptions={{ withContentSearch: true }}
            disableSavedFilters
          />
          <CustomFileTypeFilterModal />
          <OutOfWindowNotice outOfWindowCount={outOfWindowCount} />
        </>
      </FileTypeFilterProvider>
    ),
    [outOfWindowCount],
  )

  const hasFilteredResults = useMemo(
    () => !!parsedQueryParams && Object.keys(parsedQueryParams).length > 0,
    [parsedQueryParams],
  )

  const currentSelection = useMemo<MultiSelectState>(
    () => ({ includeAll: false, included: new Set<string>() }),
    [],
  )

  const onCommit = useCallback(
    async (selection: MultiSelectState) => {
      const selectedIds = selection.includeAll
        ? items.map((item) => item.id)
        : Array.from(selection.included ?? [])
      const evidenceIds = selectedIds.filter((id) => {
        const evidence = evidenceById.current.get(id)
        return evidence && !excludedEvidenceIds.has(id) && canLink(evidence)
      })
      if (!evidenceIds.length) {
        return
      }
      try {
        await onLink(evidenceIds)
      } catch (err) {
        log.error(`Error linking evidence (${title})`, err)
        showInfoToast('Error linking evidence')
      }
    },
    [items, excludedEvidenceIds, canLink, onLink, title],
  )

  return (
    <MultiSelectModal
      title={title}
      description={description}
      itemType="evidence"
      currentSelection={currentSelection}
      onCommit={onCommit}
      items={items}
      numberOfColumns={6}
      gridTemplateColumnStyling="max-content minmax(0, 1fr) max-content max-content max-content max-content"
      show={show}
      hide={hide}
      isLoading={isLoading}
      error={error}
      filters={filters}
      isFilteredResults={hasFilteredResults}
      modalSize="xl"
      headers={
        items.length ? <EvidenceIndexGridHeader isReadonly={true} /> : null
      }
      formId={modalId}
      submitText={title}
    />
  )
}
